import React, { useMemo } from "react";
import { Menu, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/images/logoMedilink_transparent.png";
import "../SideBarDoctor/SideBarDoctor.css";

const IconWrap = ({ children, className = "" }) => (
  <span className={`sb-icon ${className}`} aria-hidden>
    {children}
  </span>
);

const IAvailability = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
      <circle cx="12" cy="15" r="2" />
    </svg>
  </IconWrap>
);

const IAppointments = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
      <path d="M9 14h6M9 17h3" />
    </svg>
  </IconWrap>
);

const IPatients = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  </IconWrap>
);

const IWaitingRoom = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="9" cy="10" r="2" />
      <circle cx="15" cy="10" r="2" />
      <path d="M6 17c0-2 1.5-3 3-3h6c1.5 0 3 1 3 3" />
    </svg>
  </IconWrap>
);

const IPlaces = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M12 21s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  </IconWrap>
);

const IPlaceChild = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="5" y="5" width="14" height="14" rx="2" />
      <path d="M9 12h6" />
    </svg>
  </IconWrap>
);

const IPayment = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18M7 15h4" />
    </svg>
  </IconWrap>
);

export default function SideBarAssistant({ collapsed = false, onMenuClick }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const items = useMemo(
    () => [
      {
        key: "/assistant/availability",
        icon: <IAvailability />,
        label: t("navigation.availability"),
      },
      {
        key: "/assistant/appointments",
        icon: <IAppointments />,
        label: t("navigation.appointments"),
      },
      {
        key: "/assistant/patients",
        icon: <IPatients />,
        label: t("navigation.patients"),
      },
      {
        key: "/assistant/waiting-room",
        icon: <IWaitingRoom />,
        label: t("navigation.waitingRoom"),
      },
    
      {
        key: "/assistant/cabinet-dashboard",
        icon: <IAvailability />,
        label: t("navigation.cabinetDashboard", "Tableau de bord"),
      },
      {
        key: "assistant-places",
        icon: <IPlaces />,
        label: t("navigation.places"),
        children: [
          { key: "/assistant/pharmacies", icon: <IPlaceChild />, label: t("navigation.pharmacy") },
          { key: "/assistant/clinics", icon: <IPlaceChild />, label: t("navigation.clinics") },
          { key: "/assistant/analysis-lab", icon: <IPlaceChild />, label: t("navigation.analysisLab") },
          { key: "/assistant/nurses", icon: <IPlaceChild />, label: t("navigation.nurses") },
        ],
      },
    ],
    [t]
  );

  const withMaybeTooltip = (text, node) =>
    collapsed ? <Tooltip placement="right" title={text}>{node}</Tooltip> : node;

  const selectedKey = location.pathname;

  const builtItems = items.map((item) => ({
    ...item,
    label: withMaybeTooltip(item.label, <span className="sb__menu-label">{item.label}</span>),
    children: item.children?.map((child) => ({
      ...child,
      label: withMaybeTooltip(child.label, <span className="sb__menu-label">{child.label}</span>),
    })),
  }));

  return (
    <aside className={`sb ${collapsed ? "sb--collapsed" : ""}`}>
      <div className="sb__header">
        <img
          src={logo}
          alt="Medilink"
          className={collapsed ? "sb__logo sb__logo--collapsed" : "sb__logo"}
        />
      </div>

      <div className="sb__menu-group">
        <Menu
          className="sb__menu sb__menu--top"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          defaultOpenKeys={["assistant-places"]}
          onClick={({ key }) => {
            if (key === "assistant-places") return;
            navigate(key);
            onMenuClick?.();
          }}
          items={builtItems}
        />
      </div>
    </aside>
  );
}
